import _ from "lodash";
import { useEffect, useMemo, useState } from "react";

import useService from "../../../../hooks/useService";
import { getModalHandler } from "../../Common/Modals/hooks/useModalSingleton";
import {
  buildDirtyPatchPayload,
  buildPatchPayload,
  mapPaymentToFormState,
} from "../utils/paymentFormUtils";

export default function usePaymentsEditModal(props) {
  const { entityId, mode = "edit", openModal, paymentData, handleCloseModal, onSaveSuccess } =
    props;
  const paymentsRepository = useService("repositories/payment-repository");
  const { openFailureModal, openSuccessModal } = getModalHandler();
  const initialFormValues = useMemo(() => mapPaymentToFormState(paymentData), [paymentData]);
  const [formValues, setFormValues] = useState(initialFormValues);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (openModal) {
      setFormValues(initialFormValues);
      setIsSaving(false);
    }
  }, [initialFormValues, openModal]);

  const initialPayload = useMemo(() => buildPatchPayload(initialFormValues), [initialFormValues]);
  const currentPayload = useMemo(() => buildPatchPayload(formValues), [formValues]);
  const dirtyPayload = useMemo(
    () => buildDirtyPatchPayload(initialPayload, currentPayload),
    [currentPayload, initialPayload]
  );
  const isDirty = !_.isEqual(initialPayload, currentPayload);

  const handleFieldOnChange = (fieldName, value) => {
    setFormValues((prev) => {
      if (fieldName === "sameAsBillingAddress") {
        return {
          ...prev,
          sameAsBillingAddress: value,
          mailingAddress: value ? prev.billingAddress : prev.mailingAddress,
        };
      }

      if (fieldName === "billingAddress" && prev.sameAsBillingAddress) {
        return {
          ...prev,
          billingAddress: value,
          mailingAddress: value,
        };
      }

      return { ...prev, [fieldName]: value };
    });
  };

  const handleCancel = () => setFormValues(initialFormValues);

  const handleSave = async () => {
    if (!isDirty) {
      return;
    }

    try {
      setIsSaving(true);
      if (mode === "add") {
        await paymentsRepository.post({ ...currentPayload, entity: entityId });
      } else if (paymentData?.id) {
        await paymentsRepository.patch(paymentData.id, dirtyPayload);
      } else {
        return;
      }
      await onSaveSuccess?.();
      openSuccessModal();
      handleCloseModal();
    } catch (error) {
      openFailureModal(error);
    } finally {
      setIsSaving(false);
    }
  };

  return { formValues, isDirty, isSaving, handleFieldOnChange, handleCancel, handleSave };
}
